/**
 * Workflow Timeline Component
 * Displays all workflow steps in a vertical timeline layout
 */

import { useState, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { WorkflowStep } from './WorkflowStep';
import { WorkflowState, WorkflowStepId, StepAction } from '@/types/workflow';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConversationStore } from '@/stores/conversationStore';
import { getPlanningStatus, getSlideGenerationStatus } from '@/services/workflowAPI';
import { viewPlan, generateAudio, viewAudio, viewGallery, viewTemplate, generateVideo, viewVideo } from '@/services/workflowActions';
import { getWorkflowConfig } from '@/config/features';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface WorkflowTimelineProps {
  workflowState: WorkflowState;
  conversationId: string;
  onStepAction?: (stepId: WorkflowStepId, actionId: string) => void;
  className?: string;
}

// Step configurations with metadata
const STEP_CONFIGS = [
  {
    id: 'template_selection' as WorkflowStepId,
    title: 'Select Template',
    description: 'Choose a layout template for your video',
    stepNumber: 1,
  },
  {
    id: 'planning' as WorkflowStepId,
    title: 'Video Planning',
    description: 'Generate structured plan with story architect and layout designer',
    stepNumber: 2,
  },
  {
    id: 'slide_generation' as WorkflowStepId,
    title: 'Scene Generation',
    description: 'Create visual scenes based on the approved plan',
    stepNumber: 3,
  },
  {
    id: 'audio_generation' as WorkflowStepId,
    title: 'Audio Generation',
    description: 'Generate professional voiceover narration',
    stepNumber: 4,
  },
  {
    id: 'video_generation' as WorkflowStepId,
    title: 'Video Generation',
    description: 'Synchronize scenes with audio and generate final video manifest',
    stepNumber: 5,
  },
];

export function WorkflowTimeline({
  workflowState,
  conversationId,
  onStepAction,
  className,
}: WorkflowTimelineProps) {
  const {
    toggleStepExpanded,
    updatePlanningStatus,
    updateSlideGenerationStatus,
    updateAudioGenerationStatus,
    updateVideoGenerationStatus,
    updateStepStatus
  } = useWorkflowStore();
  const { getConversationById } = useConversationStore();
  const [planExists, setPlanExists] = useState(false);

  // Get conversation and workflow configuration
  const conversation = getConversationById(conversationId);
  const workflowConfig = getWorkflowConfig(conversation?.collectionType || 'VIDEO');

  // Filter steps based on workflow type and renumber them
  const filteredStepConfigs = STEP_CONFIGS
    .filter(config => workflowConfig.steps.includes(config.id))
    .map((config, index) => ({
      ...config,
      stepNumber: index + 1, // Renumber: 1-2 for slides, 1-5 for video
    }));

  // Ensure audio_generation step exists (for backward compatibility)
  // This automatically adds the step to old workflow states
  useEffect(() => {
    if (!workflowState.steps.audio_generation) {
      console.log('[WorkflowTimeline] Adding audio_generation step to existing workflow state');

      // Check if planning is already complete - if so, enable audio generation immediately
      const planningComplete = workflowState.steps.planning.status === 'completed';

      updateAudioGenerationStatus(conversationId, {
        status: 'pending',
        progress: 0,
        canProceed: planningComplete, // Enable if planning is done
        isExpanded: false,
      });
    }
  }, [conversationId, workflowState.steps.audio_generation, workflowState.steps.planning.status, updateAudioGenerationStatus]);

  // Ensure video_generation step exists (for backward compatibility)
  useEffect(() => {
    if (!workflowState.steps.video_generation) {
      console.log('[WorkflowTimeline] Adding video_generation step to existing workflow state');

      // Check if audio generation is already complete - if so, enable video generation immediately
      const audioComplete = workflowState.steps.audio_generation?.status === 'completed';

      updateVideoGenerationStatus(conversationId, {
        status: 'pending',
        progress: 0,
        canProceed: audioComplete, // Enable if audio is done
        isExpanded: false,
        substeps: {
          mapTiming: { status: 'pending' },
          assembly: { status: 'pending' },
          setup: { status: 'pending' },
        },
      });
    }
  }, [conversationId, workflowState.steps.video_generation, workflowState.steps.audio_generation?.status, updateVideoGenerationStatus]);

  // Poll state.json for planning status (secondary check for recovery/persistence)
  useEffect(() => {
    async function checkPlanningFromStateJson() {
      const status = await getPlanningStatus(conversationId);

      // Update local state for UI buttons
      setPlanExists(status.planCreated);

      // If state.json says planning complete but workflow doesn't know, update it
      // This handles page refresh, old projects, and other recovery scenarios
      if (status.planningComplete && workflowState.steps.planning.status !== 'completed') {
        console.log('📋 state.json indicates planning complete, updating workflow state');

        updatePlanningStatus(conversationId, {
          status: 'completed',
          progress: 100,
          completedAt: new Date(),
          canProceed: true,
        });

        // Enable slide generation step
        updateSlideGenerationStatus(conversationId, {
          canProceed: true,
        });

        // Enable audio generation step (planning creates voiceover-script.txt)
        updateAudioGenerationStatus(conversationId, {
          canProceed: true,
        });

        // Auto-expand slide_generation step
        console.log('🔽 [WorkflowTimeline] Auto-expanding slide_generation step');
        updateStepStatus(conversationId, 'slide_generation', { isExpanded: true });

        // Automatically navigate to gallery scenes tab to view the plan
        console.log('🎯 [WorkflowTimeline] Auto-navigating to gallery scenes tab');
        viewPlan(conversationId);
      }
    }

    checkPlanningFromStateJson();

    // Poll every 5 seconds for state.json updates (backend persistence)
    const interval = setInterval(checkPlanningFromStateJson, 5000);
    return () => clearInterval(interval);
  }, [conversationId, workflowState.steps.planning.status, updatePlanningStatus, updateSlideGenerationStatus]);

  // Poll state.json for slide generation progress
  useEffect(() => {
    async function checkSlideGenerationProgress() {
      // Only poll if planning is completed
      if (workflowState.steps.planning.status !== 'completed') {
        return;
      }

      console.log('🔄 [WorkflowTimeline] Polling slide generation progress...');
      const slideStatus = await getSlideGenerationStatus(conversationId);

      console.log('📊 [WorkflowTimeline] Received slide status:', {
        totalSlides: slideStatus.totalSlides,
        completedSlides: slideStatus.completedSlides,
        slidesArrayLength: slideStatus.slides.length
      });

      // Update slide generation status with current counts
      updateSlideGenerationStatus(conversationId, {
        totalSlides: slideStatus.totalSlides,
        completedSlides: slideStatus.completedSlides,
        slides: slideStatus.slides,
        progress: slideStatus.totalSlides > 0
          ? (slideStatus.completedSlides / slideStatus.totalSlides) * 100
          : 0,
      });

      console.log('✅ [WorkflowTimeline] Updated workflow store with slide data');

      // Mark as completed if all slides are done
      if (slideStatus.totalSlides > 0 && slideStatus.completedSlides === slideStatus.totalSlides) {
        if (workflowState.steps.slide_generation.status !== 'completed') {
          console.log('🎉 [WorkflowTimeline] All slides completed, marking slide generation as complete');
          updateSlideGenerationStatus(conversationId, {
            status: 'completed',
            completedAt: new Date(),
          });
        }
      }
    }

    checkSlideGenerationProgress();

    // Poll every 3 seconds for slide updates
    const interval = setInterval(checkSlideGenerationProgress, 3000);
    return () => clearInterval(interval);
  }, [conversationId, workflowState.steps.planning.status, workflowState.steps.slide_generation.status, updateSlideGenerationStatus]);

  // Poll state.json for audio generation status (persistence/recovery)
  useEffect(() => {
    async function checkAudioGenerationStatus() {
      // Check if audio_generation step exists (for backward compatibility)
      if (!workflowState.steps.audio_generation) {
        return;
      }

      // Only poll if planning is completed (audio can be generated after planning)
      if (workflowState.steps.planning.status !== 'completed') {
        return;
      }

      console.log('🔄 [WorkflowTimeline] Polling audio generation status...');

      try {
        const response = await api.get(`/conversations/${conversationId}/audio/status`);
        const audioStatus = response.data;

        console.log('📊 [WorkflowTimeline] Received audio status:', audioStatus);

        // If audio is generated in state.json but workflow doesn't know, update it
        if (audioStatus.audioGenerated && workflowState.steps.audio_generation.status !== 'completed') {
          console.log('🎙️ state.json indicates audio complete, updating workflow state');

          updateAudioGenerationStatus(conversationId, {
            status: 'completed',
            progress: 100,
            completedAt: new Date(),
            audioUrl: audioStatus.audioUrl,
            duration: audioStatus.duration,
            voiceId: audioStatus.voiceId,
            languageCode: audioStatus.language,
            canProceed: true,
          });

          // Enable video generation step after audio is complete
          console.log('📹 Enabling video generation step');
          updateVideoGenerationStatus(conversationId, {
            canProceed: true,
          });
        }

        // Separate check: If audio is complete but video generation is not yet enabled, enable it
        // This handles the case where audio is already complete when the page loads
        if (audioStatus.audioGenerated &&
            workflowState.steps.video_generation &&
            !workflowState.steps.video_generation.canProceed) {
          console.log('📹 Audio already complete, enabling video generation step');
          updateVideoGenerationStatus(conversationId, {
            canProceed: true,
          });
        }
      } catch (error) {
        console.error('Failed to check audio status:', error);
      }
    }

    checkAudioGenerationStatus();

    // Poll every 5 seconds for audio updates
    const interval = setInterval(checkAudioGenerationStatus, 5000);
    return () => clearInterval(interval);
  }, [conversationId, workflowState.steps.planning.status, workflowState.steps.audio_generation?.status, updateAudioGenerationStatus]);

  // Get action configurations for each step
  const getStepActions = (stepId: WorkflowStepId): StepAction[] => {
    switch (stepId) {
      case 'template_selection':
        return [
          {
            id: 'view_template',
            label: 'View Template',
            variant: 'outline',
            isPrimary: true,
            isEnabled: (_status) => true, // Always enabled
            handler: async (_convId, step) => {
              onStepAction?.(step, 'view_template');
            },
          },
        ];

      case 'planning':
        return [
          {
            id: 'generate_plan',
            label: 'Create Plan',
            variant: 'default',
            isPrimary: true,
            isEnabled: (status) =>
              !planExists && (status.status === 'pending' || status.status === 'error') && status.canProceed,
            handler: async (_convId, step) => {
              onStepAction?.(step, 'generate_plan');
            },
          },
          {
            id: 'reset_plan',
            label: 'Reset Plan',
            variant: 'outline',
            isEnabled: (status) =>
              planExists || status.status === 'completed' || status.status === 'error',
            handler: async (convId, step) => {
              try {
                const { resetPlanning } = await import('@/services/workflowActions');
                await resetPlanning(convId);
                onStepAction?.(step, 'reset_plan');
              } catch (error) {
                console.error('Failed to reset planning:', error);
              }
            },
          },
          {
            id: 'edit_plan',
            label: 'Edit Plan',
            variant: 'default',
            isPrimary: true,
            isEnabled: (_status) => planExists,
            handler: async (_convId, step) => {
              onStepAction?.(step, 'edit_plan');
            },
          },
          {
            id: 'view_plan',
            label: 'View Plan',
            variant: 'outline',
            isEnabled: (_status) => planExists,
            handler: async (_convId, step) => {
              onStepAction?.(step, 'view_plan');
            },
          },
        ];

      case 'slide_generation':
        return [
          {
            id: 'view_gallery',
            label: 'View Gallery',
            variant: 'outline',
            isEnabled: (_status) => true, // Always enabled - just navigates to gallery scenes tab
            handler: async (_convId, step) => {
              onStepAction?.(step, 'view_gallery');
            },
          },
        ];

      case 'audio_generation':
        return [
          {
            id: 'generate_audio',
            label: 'Generate Audio',
            variant: 'default',
            isPrimary: true,
            isEnabled: (status) =>
              status.canProceed && (status.status === 'pending' || status.status === 'error'),
            handler: async (convId, step) => {
              try {
                await generateAudio(convId);
                // Auto-navigate to audio tab to show progress
                await viewAudio(convId);
                onStepAction?.(step, 'generate_audio');
              } catch (error) {
                console.error('Failed to generate audio:', error);
              }
            },
          },
          {
            id: 'view_audio',
            label: 'View Audio',
            variant: 'outline',
            isEnabled: (status) => status.status === 'completed',
            handler: async (convId, step) => {
              await viewAudio(convId);
              onStepAction?.(step, 'view_audio');
            },
          },
        ];

      case 'video_generation':
        return [
          {
            id: 'generate_video',
            label: 'Generate Video',
            variant: 'default',
            isPrimary: true,
            isEnabled: (status) =>
              status.canProceed && (status.status === 'pending' || status.status === 'error'),
            handler: async (convId, step) => {
              try {
                await generateVideo(convId);
                onStepAction?.(step, 'generate_video');
              } catch (error) {
                console.error('Failed to generate video:', error);
              }
            },
          },
          {
            id: 'retry_video',
            label: 'Retry Video',
            variant: 'outline',
            isEnabled: (status) =>
              status.status === 'completed' || status.status === 'error',
            handler: async (convId, step) => {
              try {
                const { resetVideoGeneration } = await import('@/services/workflowActions');
                await resetVideoGeneration(convId);
                onStepAction?.(step, 'retry_video');
              } catch (error) {
                console.error('Failed to reset video generation:', error);
              }
            },
          },
        ];

      default:
        return [];
    }
  };

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const steps = workflowState.steps;

    // Count available steps (for backward compatibility with old workflow states)
    const hasAudioStep = !!steps.audio_generation;
    const hasVideoStep = !!steps.video_generation;
    const totalSteps = hasVideoStep ? 4 : hasAudioStep ? 3 : 2; // planning + slides (+ audio) (+ video)
    let completedSteps = 0;

    if (steps.planning.status === 'completed') completedSteps++;
    if (steps.slide_generation.status === 'completed') completedSteps++;
    if (hasAudioStep && steps.audio_generation.status === 'completed') completedSteps++;
    if (hasVideoStep && steps.video_generation.status === 'completed') completedSteps++;

    return {
      completed: completedSteps,
      total: totalSteps,
      percentage: Math.round((completedSteps / totalSteps) * 100),
    };
  };

  const progress = calculateOverallProgress();
  const isAllCompleted = progress.completed === progress.total;

  // Accordion behavior: close other steps when one is expanded
  const handleToggleExpand = (stepId: WorkflowStepId) => {
    const currentStepStatus = workflowState.steps[stepId];

    // Safety check for backward compatibility
    if (!currentStepStatus) {
      return;
    }

    const willBeExpanded = !currentStepStatus.isExpanded;

    if (willBeExpanded) {
      // Close all other steps
      STEP_CONFIGS.forEach((config) => {
        const stepStatus = workflowState.steps[config.id];
        if (config.id !== stepId && stepStatus && stepStatus.isExpanded) {
          updateStepStatus(conversationId, config.id, { isExpanded: false });
        }
      });

      // Auto-navigate to appropriate tab based on step
      switch (stepId) {
        case 'template_selection':
          viewTemplate(conversationId); // Navigate to Settings tab
          break;
        case 'planning':
          viewPlan(conversationId); // Navigate to Scenes tab
          break;
        case 'slide_generation':
          viewGallery(conversationId); // Navigate to Scenes tab
          break;
        case 'audio_generation':
          viewAudio(conversationId); // Navigate to Audio tab
          break;
        case 'video_generation':
          viewVideo(conversationId); // Navigate to Video tab
          break;
      }
    }

    // Toggle the clicked step
    toggleStepExpanded(conversationId, stepId);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Timeline Steps - Compact List */}
      <div className="space-y-2">
        {filteredStepConfigs.map((config, index) => {
          const status = workflowState.steps[config.id];

          // Skip rendering if step doesn't exist (backward compatibility)
          // This is temporary until the useEffect above adds the missing step
          if (!status) {
            return null;
          }

          const isCurrentStep = workflowState.currentStep === config.id;
          const isLastStep = index === filteredStepConfigs.length - 1;

          return (
            <WorkflowStep
              key={config.id}
              stepId={config.id}
              title={config.title}
              description={config.description}
              status={status}
              stepNumber={config.stepNumber}
              isCurrentStep={isCurrentStep}
              isLastStep={isLastStep}
              actions={getStepActions(config.id)}
              conversationId={conversationId}
              onToggleExpand={() => handleToggleExpand(config.id)}
            />
          );
        })}
      </div>

      {/* Completion Message - Compact */}
      {isAllCompleted && (
        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">All steps completed!</span>
        </div>
      )}
    </div>
  );
}
